/**
 * Die mitgelieferte Liste der Open-Source-Lizenzen.
 *
 * MIT, BSD, ISC und Apache-2.0 verlangen alle, dass Copyright- und Lizenztext
 * mitgeliefert werden, wenn die Software weitergegeben wird — eine ausgelieferte
 * Electron-App ist so eine Weitergabe. Angezeigt wird das unter
 * Einstellungen → Hilfe & Über → Rechtliches.
 *
 * Die JSON-Datei wird zusammen mit `docs/OPEN_SOURCE_LICENSES.md` erzeugt und
 * darf nicht von Hand gepflegt werden: `npm run licenses`.
 */
import licensesJson from './openSourceLicenses.json'

export interface OpenSourcePackage {
  /** npm-Paketname, z. B. `@xyflow/react`. */
  name: string
  version: string
  /** SPDX-Kennung, z. B. `MIT` oder `Apache-2.0`. */
  license: string
  /** Urheberzeile aus dem LICENSE/NOTICE des Pakets. Kann leer sein. */
  copyright: string
  /** Homepage oder Repository. Kann leer sein. */
  url: string
}

interface LicensesFile {
  /** Datum des letzten Generatorlaufs (ISO, nur Tag). */
  generatedAt: string
  /** HappyTask-Version zum Zeitpunkt des Laufs. */
  appVersion: string
  packages: OpenSourcePackage[]
}

const data = licensesJson as LicensesFile

export const OPEN_SOURCE_PACKAGES: OpenSourcePackage[] = data.packages
export const LICENSES_GENERATED_AT = data.generatedAt
/**
 * HappyTask-Version dieses Builds. Die Datei wird bei jedem Release neu erzeugt
 * (`/pr-release` → `npm run licenses`, geprüft von `verify-licenses`), die Zahl
 * stimmt also mit der ausgelieferten Version überein.
 */
export const APP_VERSION = data.appVersion

/**
 * Pakete nach Lizenztyp gruppiert, häufigste Lizenz zuerst, innerhalb einer
 * Gruppe alphabetisch. Dieselbe Ordnung wie in der Markdown-Fassung.
 */
export function groupByLicense(
  packages: OpenSourcePackage[] = OPEN_SOURCE_PACKAGES
): { license: string; packages: OpenSourcePackage[] }[] {
  const groups = new Map<string, OpenSourcePackage[]>()
  for (const pkg of packages) {
    const list = groups.get(pkg.license)
    if (list) list.push(pkg)
    else groups.set(pkg.license, [pkg])
  }
  return [...groups.entries()]
    .map(([license, list]) => ({
      license,
      packages: [...list].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => b.packages.length - a.packages.length || a.license.localeCompare(b.license))
}
